import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminPanel from "./AdminPanel";
import { loadTemplate, injectData, downloadHtml } from "./kosTemplate";
import { fetchPublishedData, publishData } from "./kosApi";
import "./styles.css";

/**
 * KOS Deployment Tracker — product shell.
 * Renders the self-contained dashboard inside an iframe and provides an
 * Admin entry point to refresh its data from Excel (upload or live link).
 */
export default function KOSDeploymentTracker() {
  const [srcDoc, setSrcDoc] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);

  const templateRef = useRef("");      // raw template html
  const dataRef = useRef(null);        // active dataset (null = built-in sample)

  // ---- initial load: template + any published dataset ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const template = await loadTemplate();
        if (!alive) return;
        templateRef.current = template;

        const published = await fetchPublishedData();
        if (!alive) return;

        if (published) {
          dataRef.current = published;
          setSrcDoc(injectData(template, published));
        } else {
          setSrcDoc(template); // built-in sample data
        }
      } catch (e) {
        if (alive) setError(e.message || "Failed to load the dashboard.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ---- live-apply a parsed dataset to the dashboard ----
  const applyDataset = useCallback((data) => {
    dataRef.current = data;
    if (templateRef.current) {
      setSrcDoc(injectData(templateRef.current, data));
    }
  }, []);

  // ---- publish to backend (shared default) ----
  const handlePublish = useCallback(async (data) => {
    await publishData(data);
  }, []);

  // ---- download the current dashboard as standalone html ----
  const handleDownload = useCallback(() => {
    const html = dataRef.current
      ? injectData(templateRef.current, dataRef.current)
      : templateRef.current;
    downloadHtml(html, "KOS Deployment Tracker.html");
  }, []);

  if (error) {
    return (
      <div className="kos-overlay">
        <div style={{ color: "#ff8a8a", fontWeight: 700 }}>Couldn’t load the KOS Deployment Tracker</div>
        <div>{error}</div>
      </div>
    );
  }

  return (
    <div className="kos-root">
      {loading && (
        <div className="kos-overlay">
          <div className="kos-spinner" />
          <div>Loading KOS Deployment Tracker…</div>
        </div>
      )}

      {srcDoc && (
        <iframe
          className="kos-frame"
          title="KOS Deployment Tracker"
          srcDoc={srcDoc}
          sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"
        />
      )}

      {!loading && (
        <div className="kos-fab-wrap">
          <button className="kos-fab" onClick={handleDownload} title="Download as standalone HTML">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
            </svg>
            Download
          </button>
          <button className="kos-fab primary" onClick={() => setAdminOpen(true)} title="Admin — update data">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Admin
          </button>
        </div>
      )}

      <AdminPanel
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        onApply={applyDataset}
        onPublish={handlePublish}
        onDownload={handleDownload}
      />
    </div>
  );
}
